/**
 * Favorites — the topics the user starred, kept purely locally in IndexedDB.
 *
 * The `favorites` store holds **only** `{ id }` rows: everything the list needs
 * to render (title, intro, thumbnail, completion flag) already lives in the
 * `topics_head` row for that same id, so nothing is duplicated and a favorite
 * can never go stale against the cached content.
 */

import { STORES, get, getAll, put, remove } from '../db/idb.js';
import { getTopicHead } from './content.js';

/** Is this topic favorited? */
export async function isFavorite(topicId) {
  return Boolean(await get(STORES.favorites, topicId));
}

/** Star a topic (idempotent). */
export function addFavorite(topicId) {
  return put(STORES.favorites, { id: topicId });
}

/** Un-star a topic (idempotent). */
export function removeFavorite(topicId) {
  return remove(STORES.favorites, topicId);
}

/**
 * Flip a topic's favorite state and resolve with the **new** state, so callers
 * can drive their icon from what actually landed in the store rather than from
 * a local guess.
 */
export async function toggleFavorite(topicId) {
  const next = !(await isFavorite(topicId));
  if (next) await addFavorite(topicId);
  else await removeFavorite(topicId);
  return next;
}

/**
 * The favorited topics as full `topics_head` rows, ordered by title.
 *
 * A favorite whose head row is gone (content pruned by a sync, cache cleared)
 * is skipped rather than deleted — the list just doesn't show it.
 */
export async function getFavoriteTopics() {
  const rows = await getAll(STORES.favorites);
  const heads = await Promise.all(rows.map((row) => getTopicHead(row.id)));
  return heads
    .filter(Boolean)
    .sort((a, b) => String(a.title ?? '').localeCompare(String(b.title ?? ''), 'pl'));
}
